import { useState } from "react";

import {
  Box,
  Button,
  Container,
  Grid,
  Group,
  Image,
  MediaQuery,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconArrowRight, IconChecklist } from "@tabler/icons-react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { useCTA } from "../../hooks/useCTA";
import { useCountEvent } from "../../hooks/useCountEvent";
import checklistScreenshot from "../../images/checklist-screenshot.png";
import {
  AddonTemplate,
  isAircraftAddonTemplate,
  isSoftwareAddonTemplate,
} from "../../lib/model/addonTemplate";
import { showcaseAddons } from "../../lib/state/addonTemplates";
import { librarySlice } from "../../lib/state/reducer";
import { AddonPreviewModal } from "../addons/AddonPreviewModal";
import { AddonTemplateCard } from "../addons/AddonTemplateCard";

export const HomePage = (): JSX.Element => {
  const [previewTemplate, setPreviewTemplate] = useState<AddonTemplate | null>(null);
  const navigate = useNavigate();
  const reduxDispatch = useDispatch();
  const onSelect = (t: AddonTemplate) => {
    if (isAircraftAddonTemplate(t)) {
      reduxDispatch(
        librarySlice.actions.newDocumentFromTemplate({ baseTemplate: t, softwareAddons: [] })
      );
    } else if (isSoftwareAddonTemplate(t)) {
      reduxDispatch(librarySlice.actions.insertContentsFromTemplate(t));
    }
    navigate("/editor");
  };
  const countEvent = useCountEvent();

  const cta = useCTA();

  return (
    <Container size="lg" py="md">
      <AddonPreviewModal
        template={previewTemplate}
        open={previewTemplate !== null}
        onClose={() => setPreviewTemplate(null)}
      />
      <Stack spacing="xl">
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ flex: "1 0 50%" }}>
            <MediaQuery smallerThan="sm" styles={{ fontSize: 48 }}>
              <Title sx={{ fontSize: 60, lineHeight: 1.2, fontWeight: 900 }}>
                Effortlessly build beautiful checklists
              </Title>
            </MediaQuery>
            <Title order={3} color="dimmed" mt="xl">
              Create and customize checklists for your favorite flight simulator planes and addons
              &mdash; no word processor required.
            </Title>
            <Button
              variant="gradient"
              size="md"
              radius="xl"
              mt="lg"
              leftIcon={<IconChecklist />}
              component={Link}
              to="/editor"
            >
              Get Started
            </Button>
          </Box>
          <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
            <Box sx={{ flex: "1 1 50%", position: "relative", overflow: "hidden" }}>
              <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}>
                <Image src={checklistScreenshot} height={"100%"} />
              </Box>
            </Box>
          </MediaQuery>
        </Box>
        <Stack spacing="md" mt="xl">
          <Title>Your simulator, your checklists</Title>
          <Text>Choose from dozens of templates or create your own checklist from scratch.</Text>
          <Grid>
            {showcaseAddons.map((template) => {
              return (
                <Grid.Col lg={3} md={4} sm={6} xs={6} key={template.id}>
                  <AddonTemplateCard
                    addonTemplate={template}
                    onPreview={() => {
                      setPreviewTemplate(template);
                      countEvent("preview-template-home", template.id);
                    }}
                    primaryActionLabel={cta(template)}
                    primaryAction={() => {
                      onSelect(template);
                      countEvent("start-from-template-home", template.id);
                    }}
                    linkToDetailView
                  />
                </Grid.Col>
              );
            })}
          </Grid>
          <Group align="start" pt="xl">
            <Button
              radius="xl"
              variant="gradient"
              gradient={{ from: "orange", to: "red", deg: 45 }}
              component={Link}
              to="/templates"
              rightIcon={<IconArrowRight />}
            >
              Explore more templates
            </Button>
          </Group>
        </Stack>
      </Stack>
    </Container>
  );
};
